import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ResearchResult } from 'types'
import type { TranscriptItem } from 'types'
import { clientSessionViewStore } from '../../services/clientSessionViewStore.js'
import { coerceUiLocale } from '../core/contracts.js'
import type { CanonicalSessionPermissionsV1 } from '../core/session/session-projection.js'
import {
  buildFallbackBriefingFromForm,
  buildBriefingFromResearchContext,
  buildConciergeSearchRows,
  extractFutureBriefingFromTranscript,
  isBriefingReadyToConfirm,
  shouldUseIntakeFallbackBriefing,
} from '../components/future-chat/future-chat-data-adapter'
import {
  persistIntakeIdentityToServer,
  serverVisitorHasIdentity,
  serverVisitorMatchesIntake,
} from '../lib/persist-intake-identity.js'
import {
  mintFreshClientSessionForNewVisitor,
  shouldMintFreshSessionForIntake,
  shouldSkipConciergeForVisitor,
} from '../lib/visitor-session-isolation.js'
import type {
  ConciergeFormData,
  ConciergeSession,
  ConciergeState,
  IntelligenceBriefing,
} from '../components/future-chat/future-chat-types'

const BRIEFING_CONFIRMED_KEY = 'fbc_briefing_confirmed'
/** Hold speak long enough for Living Field morph (rate ~0.026) to read as soundwave. */
export const CONCIERGE_SPEAK_LATCH_MS = 2200

const INITIAL_FORM: ConciergeFormData = {
  name: '',
  workEmail: '',
  company: '',
  role: '',
  voiceEnabled: false,
  cameraEnabled: false,
  screenEnabled: false,
  researchEnabled: true,
  locationEnabled: false,
  consentGiven: false,
}

export type ConciergePipelinePhase = 'idle' | 'submitted' | 'corrected' | 'confirmed' | 'complete'

type PipelinePhase = ConciergePipelinePhase

/**
 * Gate Living Field speak latch vs real Live handoff.
 * First-visit pipeline (`submitted` → `confirmed`) stays incomplete so
 * consent/init `hasCompletedConcierge` cannot wipe conciergeSession / speak
 * before finishFlow. `complete` always hands off to Ready Live.
 */
export function resolveConciergeFlowComplete(options: {
  phase: ConciergePipelinePhase
  returningVisitor: boolean
  formEmail: string
}): boolean {
  if (
    options.phase === 'submitted' ||
    options.phase === 'corrected' ||
    options.phase === 'confirmed'
  ) {
    return false
  }
  if (options.phase === 'complete') return true
  return (
    options.returningVisitor ||
    shouldSkipConciergeForVisitor({
      pipelinePhase: options.phase,
      formEmail: options.formEmail,
    })
  )
}

/**
 * «Velkommen tilbake» only when there is a thread to continue.
 * A failed first submit sets phase to idle after consent already wrote this
 * visitor. Without a thread that is not a return: no welcome comes, voice
 * waits for it forever. Show the form again instead.
 */
export function resolveReturningVisitor(options: {
  phase: ConciergePipelinePhase
  email: string | null | undefined
  hasThread: boolean
  projectionMatches: (email: string) => boolean
}): boolean {
  const email = options.email?.trim() ?? ''
  if (options.phase !== 'idle' || !email || !options.hasThread) return false
  return options.projectionMatches(email)
}

/**
 * Phase-driven Living Field override (does not depend on session object surviving).
 * `confirmed` → speak for the latch window; other staging phases → search.
 */
export function resolveConciergePhaseLivingMode(
  phase: ConciergePipelinePhase,
  conciergeComplete: boolean,
): 'search' | 'speak' | undefined {
  if (conciergeComplete || phase === 'complete') return undefined
  if (phase === 'confirmed') return 'speak'
  return 'search'
}

interface UseFutureConciergeFlowOptions {
  sessionId: string
  userProfile: { name: string; email: string } | null
  isResearching: boolean
  preflightFailed: boolean
  preflightTimedOut: boolean
  researchStatus: string
  transcript: TranscriptItem[]
  handleTermsComplete: (
    name: string,
    email: string,
    companyUrl?: string,
    permissions?: CanonicalSessionPermissionsV1
  ) => Promise<boolean>
}

function markBriefingConfirmed(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(BRIEFING_CONFIRMED_KEY, '1')
}

function clearBriefingConfirmed(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(BRIEFING_CONFIRMED_KEY)
}

function mapFormToPermissions(form: ConciergeFormData): CanonicalSessionPermissionsV1 {
  return {
    voice: form.voiceEnabled,
    webcam: form.cameraEnabled,
    location: form.locationEnabled,
    research: form.researchEnabled,
    screen: form.screenEnabled,
    files: false,
  }
}

export function useFutureConciergeFlow({
  sessionId,
  userProfile,
  isResearching,
  preflightFailed,
  preflightTimedOut,
  researchStatus,
  transcript,
  handleTermsComplete,
}: UseFutureConciergeFlowOptions) {
  const [formData, setFormData] = useState<ConciergeFormData>(INITIAL_FORM)
  const [phase, setPhase] = useState<PipelinePhase>('idle')
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const identitySyncInFlightRef = useRef<Promise<boolean> | null>(null)
  const [researchContext, setResearchView] = useState<ResearchResult | null>(
    () => clientSessionViewStore.getSnapshot().researchContext ?? null
  )
  const [intelligenceContext, setIntelligenceView] = useState<Record<string, unknown>>(
    () => clientSessionViewStore.getSnapshot().intelligenceContext || {}
  )

  useEffect(() => {
    const unsubscribe = clientSessionViewStore.subscribe(state => {
      setResearchView(state.researchContext ?? null)
      setIntelligenceView((state.intelligenceContext || {}) as Record<string, unknown>)
    })
    return () => {
      unsubscribe()
    }
  }, [])

  // Keep orientation empty for each visit. Stored identities are never copied into a new session.

  const briefingFromTranscript = useMemo(
    () => extractFutureBriefingFromTranscript(transcript),
    [transcript]
  )

  const briefingFromResearch = useMemo(() => {
    if (isResearching) return null
    const fromContext = buildBriefingFromResearchContext(researchContext, intelligenceContext)
    if (fromContext) return fromContext
    const embeddedResearch = intelligenceContext.research
    if (embeddedResearch && typeof embeddedResearch === 'object') {
      return buildBriefingFromResearchContext(
        embeddedResearch as ResearchResult,
        intelligenceContext
      )
    }
    return null
  }, [intelligenceContext, isResearching, researchContext])

  const effectiveBriefing: IntelligenceBriefing | null = useMemo(() => {
    if (briefingFromTranscript) return briefingFromTranscript
    if (briefingFromResearch) return briefingFromResearch
    // Intake-only fallback only after research failed/timed out — not during consent/init gap.
    if (userProfile && shouldUseIntakeFallbackBriefing({ preflightFailed, preflightTimedOut })) {
      return buildFallbackBriefingFromForm(formData, userProfile)
    }
    return null
  }, [
    briefingFromResearch,
    briefingFromTranscript,
    formData,
    preflightFailed,
    preflightTimedOut,
    userProfile,
  ])

  const searchRows = useMemo(
    () =>
      buildConciergeSearchRows({
        briefing: effectiveBriefing,
        researchStatus,
        isResearching,
      }),
    [effectiveBriefing, isResearching, researchStatus]
  )

  const hasThread = transcript.some(item => Boolean(item.text?.trim()))
  const returningVisitor = useMemo(
    () =>
      resolveReturningVisitor({
        phase,
        email: userProfile?.email,
        hasThread,
        projectionMatches: email => shouldSkipConciergeForVisitor({ pipelinePhase: 'resuming', formEmail: email }),
      }),
    // researchContext: the hydrated projection can change the match.
    [phase, researchContext, userProfile, hasThread]
  )

  const conciergeComplete = useMemo(
    () =>
      resolveConciergeFlowComplete({
        phase,
        returningVisitor,
        formEmail: formData.workEmail.trim(),
      }),
    [formData.workEmail, phase, returningVisitor]
  )

  const derivedState: ConciergeState = useMemo(() => {
    if (conciergeComplete) return 'chat_ready'
    if (phase === 'idle') return 'concierge'
    if (phase === 'confirmed') return 'brief_confirmed'
    if (phase === 'corrected') return 'brief_corrected'
    if (isBriefingReadyToConfirm(effectiveBriefing, { preflightFailed, preflightTimedOut })) {
      return 'briefing_ready'
    }
    if (phase === 'submitted' && isResearching && !researchStatus) return 'identity_submitted'
    if (phase === 'submitted' && isResearching) return 'background_search'
    if (userProfile && phase === 'submitted') return 'background_search'
    return 'concierge'
  }, [conciergeComplete, effectiveBriefing, isResearching, phase, researchStatus, userProfile])

  const searchProgress = useMemo(() => {
    if (derivedState === 'identity_submitted') return 18
    if (derivedState === 'background_search') {
      if (searchRows.length === 0) return 45
      const verified = searchRows.filter(row => row.status === 'checked').length
      return Math.min(95, 40 + verified * 12)
    }
    if (derivedState === 'briefing_ready') return 100
    return 0
  }, [derivedState, searchRows])

  const session: ConciergeSession = useMemo(
    () => ({
      state: derivedState,
      formData,
      searchRows,
      briefing: effectiveBriefing,
      searchProgress,
    }),
    [derivedState, effectiveBriefing, formData, searchProgress, searchRows]
  )

  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
    }
  }, [])

  const resolveIntakeIdentity = useCallback(
    (form?: ConciergeFormData, briefing?: IntelligenceBriefing | null) => {
      const source = form ?? formData
      const name =
        source.name.trim() ||
        userProfile?.name?.trim() ||
        briefing?.person.name?.trim() ||
        effectiveBriefing?.person.name?.trim() ||
        ''
      const email = source.workEmail.trim() || userProfile?.email?.trim() || ''
      const companyUrl =
        source.company?.trim() ||
        briefing?.strategicContext.domain?.trim() ||
        effectiveBriefing?.strategicContext.domain?.trim() ||
        undefined
      return { name, email, companyUrl }
    },
    [effectiveBriefing, formData, userProfile]
  )

  const syncIntakeIdentity = useCallback(
    async (form?: ConciergeFormData, briefing?: IntelligenceBriefing | null) => {
      const { name, email, companyUrl } = resolveIntakeIdentity(form, briefing)
      if (serverVisitorMatchesIntake(email) || (serverVisitorHasIdentity() && !email)) {
        return true
      }
      if (identitySyncInFlightRef.current) return identitySyncInFlightRef.current

      let liveSessionId = clientSessionViewStore.getSnapshot().sessionId?.trim() || sessionId
      if (!liveSessionId.trim() || !name || !email) return false

      if (shouldMintFreshSessionForIntake({ name, email })) {
        liveSessionId = mintFreshClientSessionForNewVisitor()
      }

      const source = form ?? formData
      const uiLocale = coerceUiLocale(clientSessionViewStore.getSnapshot().uiLocale) ?? undefined
      const promise = persistIntakeIdentityToServer({
        sessionId: liveSessionId,
        name,
        email,
        ...(companyUrl ? { companyUrl } : {}),
        allowResearch: source.researchEnabled,
        permissions: mapFormToPermissions(source),
        ...(uiLocale ? { uiLocale } : {}),
      }).then(result => result.ok)

      identitySyncInFlightRef.current = promise
      try {
        return await promise
      } finally {
        if (identitySyncInFlightRef.current === promise) {
          identitySyncInFlightRef.current = null
        }
      }
    },
    [formData, resolveIntakeIdentity, sessionId]
  )

  const finishFlow = useCallback(() => {
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
    markBriefingConfirmed()
    setPhase('complete')
  }, [])

  // Restore / confirm-only paths can unlock chat from sessionStorage while the
  // server projection still lacks visitor identity. Heal before the first turn.
  useEffect(() => {
    if (phase !== 'complete' && phase !== 'confirmed') return
    if (serverVisitorHasIdentity()) return
    void syncIntakeIdentity()
  }, [phase, syncIntakeIdentity, userProfile])

  const submitConcierge = useCallback(
    (form: ConciergeFormData) => {
      const researchForm = { ...form, researchEnabled: true }
      setFormData(researchForm)
      setPhase('submitted')
      const name = form.name.trim()
      const email = form.workEmail.trim()
      if (shouldMintFreshSessionForIntake({ name, email })) {
        mintFreshClientSessionForNewVisitor()
      }
      void handleTermsComplete(
        researchForm.name.trim(),
        researchForm.workEmail.trim(),
        researchForm.company?.trim() || undefined,
        mapFormToPermissions(researchForm)
      ).then(ok => {
        if (!ok) {
          setPhase('idle')
          return
        }
        // Consent landed and the opening line is already in the thread.
        // Do not keep the scanner up while research finishes.
        finishFlow()
      })
    },
    [finishFlow, handleTermsComplete]
  )

  const confirmBriefing = useCallback(() => {
    setPhase('confirmed')
    // Start latch immediately — do not wait on identity sync (can hang on /api/*).
    if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current)
    confirmTimerRef.current = setTimeout(() => {
      finishFlow()
    }, CONCIERGE_SPEAK_LATCH_MS)
    void syncIntakeIdentity(formData, effectiveBriefing)
  }, [effectiveBriefing, finishFlow, formData, syncIntakeIdentity])

  const correctBriefing = useCallback((form: ConciergeFormData) => {
    setFormData(form)
    setPhase('corrected')
  }, [])

  const resubmitCorrection = useCallback(
    (form: ConciergeFormData) => {
      const researchForm = { ...form, researchEnabled: true }
      setFormData(researchForm)
      setPhase('submitted')
      void handleTermsComplete(
        researchForm.name.trim(),
        researchForm.workEmail.trim(),
        researchForm.company?.trim() || undefined,
        mapFormToPermissions(researchForm)
      ).then(ok => {
        if (!ok) {
          setPhase('corrected')
          return
        }
        finishFlow()
      })
    },
    [finishFlow, handleTermsComplete]
  )

  /** Server rejected chat — drop stale local gate flags and reopen concierge. */
  const resetConciergeGate = useCallback(() => {
    clearBriefingConfirmed()
    setPhase('idle')
  }, [])

  const livingModeOverride = useMemo(
    () => resolveConciergePhaseLivingMode(phase, conciergeComplete),
    [conciergeComplete, phase],
  )

  return {
    conciergeComplete,
    returningVisitor,
    conciergeSession: conciergeComplete ? undefined : session,
    /** Phase-driven speak/search latch for AntigravityCanvas — survives session wipe races. */
    livingModeOverride,
    submitConcierge,
    confirmBriefing,
    correctBriefing,
    resubmitCorrection,
    resetConciergeGate,
  }
}
